import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type { RefinedFetchError, PriorityOnServerPageResponse } from '../types';
import { refineFetchError, refinePriorityOnServerPageResponse } from '../utils';

export type GetPrioritySchemeMappingsRequest = {
	/**
	 * Sets the offset of request results for pagination, will default
	 * to 0, if not set.
	 */
	startAt?: number;
	/**
	 * Sets the max amount of results from the request for pagination,
	 * will default to 50 if not set.
	 */
	maxResults?: number;
	/**
	 * The scheme id, we want mappings for, if undefined we are creating a
	 * new scheme.
	 */
	schemeId?: number;
	/**
	 * Priorities that being changed for an edit or create priority
	 * scheme request.
	 */
	priorities?: {
		add?: number[];
		remove?: number[];
	};
	/**
	 * Projects that are being changed for an edit or create priority
	 * scheme request. For projects, we only care about projects being added
	 * when getting mappings, as removing a project will never need mappings
	 * for the current scheme.
	 *
	 * Instead, if a project is being removed from the current scheme, we will
	 * make a separate request for the default scheme (or in future the scheme
	 * a user may want to map to), where these priorities are being added to that
	 * scheme in order to get the mappings we need to do. i.e. we are doing
	 * the inverse operation.
	 */
	projects?: {
		add?: number[];
	};
};

export const getPrioritySchemeMappingsRequest = async (
	requestParams: GetPrioritySchemeMappingsRequest,
): Promise<PriorityOnServerPageResponse> => {
	try {
		const URL = '/rest/api/2/priorityscheme/mappings';
		const response: unknown = await fetchJson(URL, {
			method: 'POST',
			body: JSON.stringify(requestParams),
		});

		const responseBody = refinePriorityOnServerPageResponse(response);

		if (!responseBody) {
			throw new Error('Invalid response');
		}

		return responseBody;
	} catch (error) {
		// We don't care if the error is a 400 or 403 we will deal with them in
		// in the same way i imagine.
		const fetchError: RefinedFetchError | undefined = await refineFetchError(error);
		fireErrorAnalytics({
			error: fetchError
				? new Error(
						`get priority scheme mappings failed with status code ${fetchError.statusCode} and response body ${fetchError.responseJson}`,
					)
				: new Error('get priority scheme mappings failed with unexpected error'),
			meta: {
				id: 'getPrioritySchemeMappings',
				packageName: 'jiraAdminPagesPrioritySchemes',
				teamName: 'morphinecake',
			},
			sendToPrivacyUnsafeSplunk: true,
		});

		throw error;
	}
};
